import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { completePendingAction } from '../utils/pendingAction';
import GoogleLoginButton from '../components/GoogleLoginButton';
import PasswordInput from '../components/PasswordInput';
import { validateIndianMobile } from '../utils/validation';
import './Auth.css';

const Signup = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading: authLoading } = useAuth();

  // If already logged in, redirect to home (replace history so back button doesn't return to signup)
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ phone: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') setFieldErrors(prev => ({ ...prev, phone: '' }));
    let finalValue = value;
    if (name === 'phone') finalValue = value.replace(/\D/g, '').slice(0, 12);
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (formData.phone && formData.phone.trim()) {
      const phoneValidation = validateIndianMobile(formData.phone);
      if (!phoneValidation.valid) {
        setFieldErrors(prev => ({ ...prev, phone: phoneValidation.message }));
        toast.error(phoneValidation.message);
        return;
      }
    }
    setFieldErrors(prev => ({ ...prev, phone: '' }));

    setLoading(true);

    try {
      const response = await api.post('/user/auth/signup', {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null,
      });
      login(response.token, response.user);
      toast.success('Account created successfully');
      const didComplete = await completePendingAction(navigate);
      if (!didComplete) navigate('/');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.error || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  if (!authLoading && isAuthenticated) {
    return null; // redirecting in useEffect
  }

  return (
    <div className="auth-page">
      <Container>
        <div className="auth-card">
          <h1 className="auth-title">Create Your Account</h1>
          <p className="auth-subtitle">Join Us To Explore A Curated World Of Men's Modern Fashion</p>

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="John Doe"
                className="auth-input"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Johndoe@Gmail.Com"
                className="auth-input"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <PasswordInput
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Password"
                minLength={6}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <PasswordInput
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder="Confirm Password"
                minLength={6}
              />
            </Form.Group>

            <Form.Group className="mb-8">
              <Form.Label>Phone Number (Optional)</Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="10 digits (6–9)"
                maxLength={12}
                className={`auth-input ${fieldErrors.phone ? 'border-danger' : ''}`}
              />
              {fieldErrors.phone && (
                <Form.Text className="text-danger d-block">{fieldErrors.phone}</Form.Text>
              )}
            </Form.Group>

            <Button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? 'Creating Account...' : 'CREATE ACCOUNT'}
            </Button>
          </Form>

          <div className="auth-switch">
            <p>
              Already Have An Account? <Link to="/login">Login</Link>
            </p>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Signup;

